import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Chip,
    Divider,
    Alert,
    IconButton,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import LinkIcon from '@mui/icons-material/Link';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import moment from 'moment';
import {
    searchPotentialMatchesRequest,
    linkStudyToOrderRequest,
    resetSearchPotentialMatches,
    resetLinkStudy,
} from '../../redux/modules/radiology/slice';

const StyledListItem = styled(ListItem)(({ theme }) => ({
    borderRadius: 12,
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    transition: 'all 150ms ease',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
        borderColor: theme.palette.primary.light,
    },
}));

const ScoreChip = styled(Chip)(({ theme, score }) => {
    let color = theme.palette.grey[500];
    let bg = alpha(theme.palette.grey[500], 0.1);

    if (score >= 80) {
        color = theme.palette.success.dark;
        bg = alpha(theme.palette.success.main, 0.1);
    } else if (score >= 50) {
        color = theme.palette.warning.dark;
        bg = alpha(theme.palette.warning.main, 0.1);
    } else if (score >= 30) {
        color = theme.palette.info.dark;
        bg = alpha(theme.palette.info.main, 0.1);
    }

    return {
        backgroundColor: bg,
        color: color,
        fontWeight: 600,
        fontSize: '0.75rem',
    };
});

const InfoRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    marginTop: theme.spacing(0.5),
}));

export default function LinkDicomDialog({ open, onClose, appointment, onSuccess }) {
    const dispatch = useDispatch();
    const [linking, setLinking] = useState(false);
    const [selectedStudy, setSelectedStudy] = useState(null);

    const {
        searchPotentialMatchesRes,
        searchPotentialMatchesLoading,
        linkStudyRes,
        linkStudyLoading,
    } = useSelector(state => state.radiology);

    // Search for potential matches when dialog opens
    useEffect(() => {
        if (open && appointment) {
            const patientName = appointment.patientId
                ? `${appointment.patientId.fName} ${appointment.patientId.lName}`
                : '';
            const studyDate = appointment.appoinmentId?.startTime
                ? moment(appointment.appoinmentId.startTime).format('YYYY-MM-DD')
                : '';
            const accessionNumber = appointment.orderId?.accessionNumber || '';

            dispatch(searchPotentialMatchesRequest({
                patientName,
                studyDate,
                accessionNumber,
            }));
        }

        return () => {
            dispatch(resetSearchPotentialMatches());
            dispatch(resetLinkStudy());
        };
    }, [open, appointment, dispatch]);

    // Handle link success
    useEffect(() => {
        if (linkStudyRes?.success) {
            onSuccess?.();
            handleClose();
        }
    }, [linkStudyRes, onSuccess]);

    const handleClose = () => {
        setSelectedStudy(null);
        setLinking(false);
        dispatch(resetSearchPotentialMatches());
        dispatch(resetLinkStudy());
        onClose();
    };

    const handleLinkStudy = (study) => {
        if (!appointment?.orderId?._id) return;

        setLinking(true);
        setSelectedStudy(study._id);
        dispatch(linkStudyToOrderRequest({
            unmatchedStudyId: study._id,
            orderId: appointment.orderId._id,
        }));
    };

    const matches = searchPotentialMatchesRes?.data || [];
    const hasError = searchPotentialMatchesRes?.success === false;
    const linkError = linkStudyRes?.success === false;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3 }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinkIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>Link DICOM Study</Typography>
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {/* Patient info */}
                {appointment && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Searching matches for:
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {appointment.patientId?.fName} {appointment.patientId?.lName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Order: {appointment.orderId?.orderId} |
                            Date: {moment(appointment.appoinmentId?.startTime).format('MMM DD, YYYY')}
                        </Typography>
                    </Box>
                )}

                {/* Loading state */}
                {searchPotentialMatchesLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Error state */}
                {hasError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {searchPotentialMatchesRes?.message || 'Failed to search for matches'}
                    </Alert>
                )}

                {linkError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {linkStudyRes?.message || 'Failed to link study'}
                    </Alert>
                )}

                {/* No matches */}
                {!searchPotentialMatchesLoading && !hasError && matches.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <ImageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                        <Typography color="text.secondary">
                            No unmatched DICOM studies found
                        </Typography>
                        <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                            DICOM images may not have been uploaded yet, or they may have been automatically matched.
                        </Typography>
                    </Box>
                )}

                {/* Matches list */}
                {!searchPotentialMatchesLoading && matches.length > 0 && (
                    <>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Found {matches.length} potential match{matches.length !== 1 ? 'es' : ''}:
                        </Typography>
                        <List sx={{ p: 0 }}>
                            {matches.map((study, index) => (
                                <StyledListItem
                                    key={study._id}
                                    sx={{ pr: 14 }}
                                >
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Typography variant="body1" fontWeight={500}>
                                                    {study.patientName || 'Unknown Patient'}
                                                </Typography>
                                                <ScoreChip
                                                    label={`${study.score}% match`}
                                                    size="small"
                                                    score={study.score}
                                                />
                                            </Box>
                                        }
                                        secondary={
                                            <Box>
                                                <InfoRow>
                                                    <CalendarTodayIcon sx={{ fontSize: 14 }} />
                                                    {study.studyDate
                                                        ? moment(study.studyDate).format('MMM DD, YYYY')
                                                        : 'Unknown date'
                                                    }
                                                    <Box component="span" sx={{ mx: 1, color: 'divider' }}>|</Box>
                                                    <ImageIcon sx={{ fontSize: 14 }} />
                                                    {study.instanceCount || 0} images
                                                    <Box component="span" sx={{ mx: 1, color: 'divider' }}>|</Box>
                                                    {study.modality || 'US'}
                                                </InfoRow>
                                                {study.matchReasons?.length > 0 && (
                                                    <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                                                        {study.matchReasons.join(', ')}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={linkStudyLoading && selectedStudy === study._id ? <CircularProgress size={16} /> : <LinkIcon />}
                                            onClick={() => handleLinkStudy(study)}
                                            disabled={linkStudyLoading}
                                            sx={{ borderRadius: 2 }}
                                        >
                                            Link
                                        </Button>
                                    </ListItemSecondaryAction>
                                </StyledListItem>
                            ))}
                        </List>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} disabled={linkStudyLoading}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
